import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, DialogContent, IconButton, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, LineChart, RotateCcw, Sparkles, X } from "lucide-react";

import { Dialog } from "@/components/Dialog";
import { AlertNote } from "@/components/AlertNote";
import { Button } from "@/components/Button";
import { brand } from "@/theme/theme";
import {
  KEYWORDS_PER_ENGINE,
  MAX_COMPETITORS,
  getRankTrackingConfig,
  parseProvisionError,
  provisionRankTracking,
  rankTrackingKeys,
  updateRankTracking,
} from "@/services/rankTrackingService";
import { SetupStepperHeader } from "./components";
import { useSeoSetupClient, useSeoSetupDraft } from "./hooks";
import {
  STEP_KEYS,
  buildProvisionPayload,
  diffRemovals,
  draftFromConfig,
  emptyDraft,
  extractHost,
  keywordEngines,
  validateStep,
} from "./seoSetupUtils";
import { GeneralInfoStep } from "./steps/GeneralInfoStep";
import { SearchEnginesStep } from "./steps/SearchEnginesStep";
import { KeywordsStep } from "./steps/KeywordsStep";
import { CompetitorsStep } from "./steps/CompetitorsStep";
import { AnalyticsStep } from "./steps/AnalyticsStep";
import { ProvisionProgress, ReviewSummary } from "./steps/ReviewAndProgress";

const STEP_TITLES = {
  general: "Información general",
  engines: "Motores",
  keywords: "Keywords",
  competitors: "Competidores",
  analytics: "Analítica",
  review: "Revisar",
};

const REVIEW_INDEX = STEP_KEYS.indexOf("review");
const SERVER_BLOCKS = ["general", "engines", "keywords", "competitors"];

// Same props as ConnectSeRankingModal. With `editLink` the draft starts from the applied
// config and goes to `rank-tracking/update/`, which removes whatever it leaves out.
export function SeoProjectSetupModal({ open, onOpenChange, clientId, client: clientProp, onConnected, editLink }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const bodyRef = useRef(null);

  const clientQuery = useSeoSetupClient(clientId, { enabled: open && !clientProp?.name });
  const client = clientProp?.name ? clientProp : clientQuery.data || clientProp || null;

  const isEdit = Boolean(editLink);
  // Its own storage key, so an edit never shows up as a setup left halfway.
  const storageId = isEdit ? `edit-${clientId}` : clientId;
  const { draft, update, applyDefaults, persist, clearStorage, replace, restored } = useSeoSetupDraft(storageId);

  const configQuery = useQuery({
    queryKey: rankTrackingKeys.config(clientId),
    queryFn: () => getRankTrackingConfig(clientId),
    enabled: open && isEdit && Boolean(clientId),
    staleTime: 0,
    retry: false,
  });
  const appliedConfig = configQuery.data?.config || null;

  const [attempted, setAttempted] = useState({});
  const [serverErrors, setServerErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [failure, setFailure] = useState("");
  const [hideRestoredNotice, setHideRestoredNotice] = useState(false);
  // In memory only: "Reintentar lo que falta" resends exactly this config.
  const lastPayloadRef = useRef(null);
  const lastDraftRef = useRef(null);

  // An edit starts from the applied config each time it opens.
  const loadedConfigRef = useRef(null);
  useEffect(() => {
    if (!open) {
      loadedConfigRef.current = null;
      return;
    }
    if (!appliedConfig || loadedConfigRef.current === appliedConfig) return;
    loadedConfigRef.current = appliedConfig;
    replace(draftFromConfig(appliedConfig));
    setAttempted({});
    setServerErrors({});
  }, [open, appliedConfig, replace]);

  // Client name and website prefill step 1 unless the draft already has them.
  useEffect(() => {
    if (!client || isEdit) return;
    applyDefaults((prev) => ({
      general: {
        url: prev.general?.url || client.website || "",
        title: prev.general?.title || client.name || "",
      },
    }));
  }, [client?.website, client?.name, applyDefaults, isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  // Until the applied config is in the draft there is nothing to edit.
  const editLoading = isEdit && (configQuery.isLoading || (appliedConfig && loadedConfigRef.current !== appliedConfig));
  let editLoadError = "";
  if (isEdit && !configQuery.isLoading && !appliedConfig) {
    editLoadError = configQuery.isError
      ? parseProvisionError(configQuery.error).message
      : "No se ha encontrado la configuración de este proyecto.";
  }
  const showProgress = isRunning || Boolean(result || failure);
  const resultStatus = failure ? "error" : result?.status;

  const stepIndex = Math.min(Math.max(Number(draft.step) || 0, 0), STEP_KEYS.length - 1);
  const stepKey = STEP_KEYS[stepIndex];
  const stepErrors = useMemo(
    () =>
      STEP_KEYS.map((key) => validateStep(key, draft, { perEngine: KEYWORDS_PER_ENGINE, competitors: MAX_COMPETITORS })),
    [draft]
  );
  const isValid = (index) => Object.keys(stepErrors[index]).length === 0;
  const clientHost = extractHost(draft.general?.url);

  useEffect(() => {
    bodyRef.current?.scrollTo?.({ top: 0 });
  }, [stepIndex, showProgress]);

  const goTo = (index) => {
    setHideRestoredNotice(true);
    update({ step: index });
  };
  const markAttempted = (index) => setAttempted((prev) => ({ ...prev, [index]: true }));

  const handleNext = () => {
    if (isValid(stepIndex)) goTo(stepIndex + 1);
    else markAttempted(stepIndex);
  };

  const resetRun = () => {
    setResult(null);
    setFailure("");
  };

  // The request goes on server side if the dialog closes, so it stays open until it answers.
  const handleClose = () => {
    if (isRunning) return;
    resetRun();
    onOpenChange(false);
  };

  const handleResult = (data) => {
    setResult(data);
    clearStorage();
    queryClient.invalidateQueries({ queryKey: ["client-connection", "seranking"] });
    queryClient.invalidateQueries({ queryKey: rankTrackingKeys.config(clientId) });
    // Same shape as a link row, enough for the callers until they refetch it.
    if (data.link_id) {
      const general = lastPayloadRef.current?.general;
      onConnected?.({
        id: data.link_id,
        client: Number(clientId),
        site_id: data.site_id,
        site_title: general?.title || "",
        site_domain: extractHost(general?.url),
        managed: true,
      });
    }
  };

  const launch = async (payload) => {
    lastPayloadRef.current = payload;
    resetRun();
    setIsRunning(true);
    try {
      handleResult(await (isEdit ? updateRankTracking(payload) : provisionRankTracking(payload)));
    } finally {
      setIsRunning(false);
    }
  };

  const handleSubmit = async () => {
    const firstInvalid = STEP_KEYS.findIndex((_, index) => !isValid(index));
    if (firstInvalid !== -1 && firstInvalid < REVIEW_INDEX) {
      markAttempted(firstInvalid);
      goTo(firstInvalid);
      return;
    }
    setServerErrors({});
    setSubmitError("");
    try {
      lastDraftRef.current = draft;
      await launch(buildProvisionPayload(clientId, draft));
    } catch (err) {
      // 400s land on the step they belong to, 403/409 stay on review, the rest end the run.
      const parsed = parseProvisionError(err);
      const firstBlock = SERVER_BLOCKS.find((block) => parsed.blocks[block]);
      if (firstBlock) {
        setServerErrors(parsed.blocks);
        goTo(STEP_KEYS.indexOf(firstBlock));
      } else if (parsed.status === 400 || parsed.status === 403 || parsed.status === 409) {
        setSubmitError(parsed.message);
      } else {
        setFailure(parsed.message);
      }
    }
  };

  const handleRetryMissing = async () => {
    try {
      // Without `retry` the backend answers 409 because the managed site already exists.
      // An edit just resends its draft: it is the whole desired state anyway.
      await launch(isEdit ? lastPayloadRef.current : { ...lastPayloadRef.current, retry: true });
    } catch (err) {
      setFailure(parseProvisionError(err).message);
    }
  };

  // Back to the form with the config that was sent.
  const handleRestore = () => {
    resetRun();
    if (lastDraftRef.current) replace({ ...lastDraftRef.current, step: REVIEW_INDEX });
  };

  const handleStartOver = () => {
    clearStorage();
    replace({ ...emptyDraft(), general: { url: client?.website || "", title: client?.name || "" } });
    setAttempted({});
    setServerErrors({});
    setHideRestoredNotice(true);
  };

  const headerSteps = STEP_KEYS.map((key, index) => {
    let state = "pending";
    if (index === stepIndex) state = "active";
    else if (serverErrors[key]?.length || (attempted[index] && !isValid(index))) state = "error";
    else if (index < stepIndex && isValid(index)) state = "done";
    return { key, title: STEP_TITLES[key], state };
  });

  const stepProps = {
    errors: stepErrors[stepIndex],
    showErrors: Boolean(attempted[stepIndex]),
    serverErrors: serverErrors[stepKey],
    onChange: (patch) => {
      if (serverErrors[stepKey]) setServerErrors((prev) => ({ ...prev, [stepKey]: undefined }));
      update(patch);
    },
  };

  const renderStep = () => {
    switch (stepKey) {
      case "general":
        return (
          <GeneralInfoStep
            {...stepProps}
            lockUrl={isEdit}
            general={draft.general}
            onChange={(patch) => stepProps.onChange((prev) => ({ general: { ...prev.general, ...patch } }))}
          />
        );
      case "engines":
        return (
          <SearchEnginesStep {...stepProps} engines={draft.engines} brand={draft.brand} keywords={draft.keywords} />
        );
      case "keywords":
        return (
          <KeywordsStep
            {...stepProps}
            engines={keywordEngines(draft.engines)}
            keywords={draft.keywords}
            limit={KEYWORDS_PER_ENGINE}
          />
        );
      case "competitors":
        return (
          <CompetitorsStep {...stepProps} competitors={draft.competitors} clientHost={clientHost} max={MAX_COMPETITORS} />
        );
      case "analytics":
        return <AnalyticsStep clientId={clientId} client={client} onBeforeConnect={persist} />;
      default:
        return (
          <ReviewSummary
            draft={draft}
            clientId={clientId}
            onEditStep={goTo}
            serverError={submitError}
            isEdit={isEdit}
            removals={isEdit && appliedConfig ? diffRemovals(appliedConfig, buildProvisionPayload(clientId, draft)) : null}
          />
        );
    }
  };

  const progressFooter = (
    <>
      <Box sx={{ display: "flex", gap: 1, ml: "auto", flexWrap: "wrap", justifyContent: "flex-end" }}>
        <Button variant="outline" onClick={handleClose} disabled={isRunning}>
          Cerrar
        </Button>
        {!isRunning && resultStatus === "active" && (
          <Button
            onClick={() => {
              handleClose();
              navigate(`/analiticas?clientId=${clientId}&tab=visibilidad`);
            }}
          >
            <LineChart size={16} />
            Ir a Visibilidad
          </Button>
        )}
        {!isRunning && resultStatus === "partial" && lastPayloadRef.current && (
          <Button onClick={handleRetryMissing}>
            <RotateCcw size={16} />
            Reintentar lo que falta
          </Button>
        )}
        {!isRunning && resultStatus === "error" && (
          <Button onClick={handleRestore}>
            <RotateCcw size={16} />
            Volver a intentarlo
          </Button>
        )}
      </Box>
    </>
  );

  const stepFooter = (
    <>
      <Button
        variant="ghost"
        onClick={() => {
          if (!isEdit) persist();
          handleClose();
        }}
        sx={{ color: "text.secondary", px: { xs: 1, sm: 2 } }}
      >
        {isEdit ? "Cancelar" : "Omitir por ahora"}
      </Button>
      <Typography
        variant="body2"
        sx={{ flex: 1, textAlign: "center", color: "text.secondary", display: { xs: "none", sm: "block" } }}
      >
        <Box component="strong" sx={{ color: brand.ink }}>
          {stepIndex + 1}
        </Box>
        {` / ${STEP_KEYS.length}`}
      </Typography>
      <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
        <Button
          variant="outline"
          onClick={() => goTo(stepIndex - 1)}
          disabled={stepIndex === 0}
          aria-label="Atrás"
          sx={{ px: { xs: 1.5, sm: 2.25 } }}
        >
          <ArrowLeft size={16} />
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            Atrás
          </Box>
        </Button>
        {stepKey === "review" ? (
          <Button onClick={handleSubmit}>
            <Sparkles size={16} />
            {isEdit ? "Guardar cambios" : "Crear proyecto"}
          </Button>
        ) : (
          <Button onClick={handleNext}>
            Siguiente
            <ArrowRight size={16} />
          </Button>
        )}
      </Box>
    </>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : handleClose())}
      disableBackdropClose
      fullWidth
      fullScreen={fullScreen}
      maxWidth="md"
      aria-labelledby="seo-setup-title"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : "16px",
          boxShadow: theme.shadows[4],
          display: "flex",
          flexDirection: "column",
          height: fullScreen ? "100%" : "min(800px, calc(100% - 64px))",
          maxWidth: fullScreen ? "100%" : 880,
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 }, pb: 2, borderBottom: "1px solid #E8ECF2" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: showProgress ? 0 : 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              minWidth: 40,
              borderRadius: "12px",
              background: brand.gradientDiagonal,
              color: "#fff",
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LineChart size={20} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography id="seo-setup-title" component="h2" sx={{ fontSize: "1.125rem", fontWeight: 700, color: brand.ink }}>
              {isEdit ? "Editar seguimiento SEO" : "Configurar seguimiento SEO"}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {[client?.name, clientHost || extractHost(client?.website)].filter(Boolean).join(" · ") || "Cliente"}
            </Typography>
          </Box>
          <IconButton aria-label="Cerrar" onClick={handleClose} size="small" sx={{ mt: -0.5, mr: -0.5 }}>
            <X size={18} />
          </IconButton>
        </Box>
        {!showProgress && !editLoading && (
          <SetupStepperHeader
            steps={headerSteps}
            activeIndex={stepIndex}
            canSelect={(index) => index < stepIndex || STEP_KEYS.slice(0, index).every((_, i) => isValid(i))}
            onSelect={goTo}
          />
        )}
      </Box>

      <DialogContent ref={bodyRef} sx={{ flex: 1, overflowY: "auto", px: { xs: 2, sm: 3 }, py: 2.5, backgroundColor: "#fff" }}>
        {!showProgress && !isEdit && restored && !hideRestoredNotice && (
          <AlertNote
            variant="info"
            sx={{ mb: 2.5 }}
            action={
              <Button size="xs" variant="ghost" onClick={handleStartOver}>
                Empezar de nuevo
              </Button>
            }
          >
            Hemos recuperado la configuración que dejaste a medias.
          </AlertNote>
        )}
        {showProgress ? (
          <ProvisionProgress
            isEdit={isEdit}
            isRunning={isRunning}
            result={result}
            failure={failure}
            engines={lastDraftRef.current?.engines}
          />
        ) : editLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : editLoadError ? (
          <AlertNote variant="danger">{editLoadError}</AlertNote>
        ) : (
          renderStep()
        )}
      </DialogContent>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: { xs: 2, sm: 3 },
          py: 1.75,
          borderTop: "1px solid #E8ECF2",
          backgroundColor: "#FAFBFC",
          pb: fullScreen ? "max(14px, env(safe-area-inset-bottom))" : 1.75,
        }}
      >
        {showProgress ? progressFooter : editLoading || editLoadError ? (
          <Button variant="outline" onClick={handleClose} sx={{ ml: "auto" }}>
            Cerrar
          </Button>
        ) : (
          stepFooter
        )}
      </Box>
    </Dialog>
  );
}

SeoProjectSetupModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  clientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  client: PropTypes.shape({ name: PropTypes.string, website: PropTypes.string, logo: PropTypes.string }),
  onConnected: PropTypes.func,
  editLink: PropTypes.shape({ id: PropTypes.number, managed: PropTypes.bool }),
};

export default SeoProjectSetupModal;
